import { useState, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSelector, shallowEqual } from 'react-redux'
import { selectUserToken, selectUserUserId, selectCompanyId } from '../store/user'
import { translateCheckinToBoardData } from '../clients/tools'

import { driverClient } from '../clients/DriverClients'

import {
    CheckIn,
    BoardCheckinData,
    CheckinStatus,
    UpdateCheckinStatusData,
    UpdateCheckinData,
    CardContent,
} from '../models/client/checkins'

type Return = {
    initialBoardData: BoardCheckinData
    isLoading: boolean
    checkinStatus: CheckinStatus[]
    dockOptions: Array<any>
    boardData: BoardCheckinData
    refresh: boolean
    fuelTypes: { [key: string]: { value: string } }
    setRefresh: (data: boolean) => void
    setSiteId: (data: string) => void
    siteId: string
    setLocationId: (data: string) => void
    locationId: string
    setBoardData: (data: BoardCheckinData) => void
    isAllowedMoving: ({ prevId, nextId }: { prevId: string; nextId: string }) => boolean
    updateCheckinData: (checkinId: string, params: UpdateCheckinData) => Promise<any>
}

type Params = {
    siteId: string
}

export const useDriverCheckins = (yardType: string): Return => {
    const [boardData, setBoardData] = useState({
        lanes: [],
    })
    const [refresh, setRefresh] = useState(false)
    const [intervalMs, setIntervalMs] = useState(20000)
    const token = useSelector(selectUserToken, shallowEqual)
    const companyId = useSelector(selectCompanyId, shallowEqual)
    const [siteId, setSiteId] = useState(null)
    const [locationId, setLocationId] = useState(null)
    const CHECKIN_LIST_QUERY_KEY = useMemo(
        () => ['getDriverCheckins', token, companyId, siteId, locationId],
        [token, companyId, siteId, locationId]
    )
    const CHECKIN_STATUS_QUERY_KEY = useMemo(() => ['getCheckinStatus', token], [token])
    const DOCK_QUERY_KEY = useMemo(
        () => ['getDockingOptions', token, companyId, siteId],
        [token, companyId, siteId]
    )
    const FUEL_QUERY_KEY = useMemo(() => ['getFuelTypes', token], [token])

    const { data: checkinStatus, status: checkinApiStatus } = useQuery(
        CHECKIN_STATUS_QUERY_KEY,
        () => {
            if (!token) return []
            return driverClient.getCheckinStatusList({ token })
        },
        {
            enabled: true,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        }
    )

    const {
        data: checkins,
        status: checkinListApiStatus,
        refetch,
    } = useQuery(
        CHECKIN_LIST_QUERY_KEY,
        () => {
            if (!token || !siteId) return []
            return driverClient.getCheckinLists({ token, companyId, siteId, locationId })
        },
        {
            enabled: true,
            refetchOnWindowFocus: true,
            keepPreviousData: true,
            refetchInterval: intervalMs,
            notifyOnChangeProps: ['data', 'error'],
        }
    )

    const { data: fuelTypes } = useQuery(
        FUEL_QUERY_KEY,
        () => {
            if (!token) return []
            return driverClient.getFuelTypes()
        },
        {
            enabled: true,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        }
    )

    const initialBoardData = useMemo(
        () => translateCheckinToBoardData(checkinStatus, checkins, yardType),
        [checkinStatus, checkins, refresh]
    )

    const { mutateAsync: updateCheckinData, status: updateDataStatus } = useMutation(
        driverClient.updateCheckin
    )

    const { data: dockOptions, status: dockApiStatus } = useQuery(
        DOCK_QUERY_KEY,
        () => {
            if (!token) return []
            return driverClient.getDockingOptions({ token, companyId, siteId })
        },
        {
            enabled: true,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        }
    )

    const isAllowedMoving = ({ prevId, nextId }) => {
        const statusMap = initialBoardData.lanes.map(
            (val: { id: string; title: string; label: string; cards: CardContent[] }) => {
                return val.id
            }
        )

        if (statusMap.indexOf(prevId) > statusMap.indexOf(nextId)) return false

        return true
    }

    const isLoading = checkinApiStatus === 'loading' || checkinListApiStatus === 'loading'

    return {
        initialBoardData,
        isLoading,
        boardData,
        refresh,
        setRefresh,
        checkinStatus,
        fuelTypes,
        dockOptions,
        isAllowedMoving,
        setBoardData,
        siteId,
        setSiteId,
        locationId,
        setLocationId,
        async updateCheckinData(checkinId, params) {
            if (!token) return []
            try {
                await updateCheckinData({
                    token,
                    checkinId,
                    params,
                })
                await refetch()
            } catch (e) {
                if (e instanceof Error) {
                    throw new Error('err')
                }
            }
        },
    }
}
